import React from "react";
import {
  Document,
  Page,
  Text,
  View,
  StyleSheet,
  Image,
} from "@react-pdf/renderer";
import { InvoiceData } from "../../models/invoice/invoice.model";
import { IOrderAddress } from "../../models/Order.model";
import logo from "../../assets/images/login_logo.png";
import stamp from "../../assets/signs/stamp-1.png";

const styles = StyleSheet.create({
  page: {
    paddingTop: 20,
    paddingBottom: 24,
    paddingHorizontal: 30,
    fontSize: 8,
    fontFamily: "Helvetica",
    color: "#1f2937",
    backgroundColor: "#ffffff",
  },
  ewbPage: {
    paddingBottom: 48,
  },
  topLine: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    paddingBottom: 7,
    marginBottom: 8,
    borderBottomWidth: 2,
    borderBottomColor: "#d1d5db",
  },
  logo: {
    width: 78,
    height: 26,
    objectFit: "contain",
    marginBottom: 5,
  },
  documentTitle: {
    fontSize: 18,
    fontFamily: "Helvetica-Bold",
    color: "#111827",
  },
  documentSubtitle: {
    marginTop: 3,
    fontSize: 7,
    color: "#4b5563",
    fontFamily: "Helvetica-Bold",
    textTransform: "uppercase",
  },
  row: {
    flexDirection: "row",
    gap: 10,
  },
  bold: {
    fontFamily: "Helvetica-Bold",
  },
  seller: {
    flex: 1,
  },
  sellerName: {
    fontSize: 9.5,
    fontFamily: "Helvetica-Bold",
    color: "#111827",
    marginBottom: 4,
  },
  muted: {
    color: "#6b7280",
    fontSize: 7,
    lineHeight: 1.35,
  },
  qrBlock: {
    width: 96,
    alignItems: "center",
  },
  qrImage: {
    width: 86,
    height: 86,
  },
  sectionHeading: {
    color: "#4b5563",
    fontSize: 7,
    fontFamily: "Helvetica-Bold",
    textTransform: "uppercase",
    marginBottom: 4,
  },
  metaGrid: {
    flexDirection: "row",
    marginTop: 7,
    marginBottom: 7,
    borderWidth: 1,
    borderColor: "#d1d5db",
    borderRadius: 5,
    backgroundColor: "#ffffff",
  },
  metaCell: {
    flexGrow: 1,
    flexBasis: 0,
    padding: 6,
    borderRightWidth: 1,
    borderRightColor: "#d1d5db",
  },
  metaLabel: {
    color: "#6b7280",
    fontSize: 6.5,
    textTransform: "uppercase",
    marginBottom: 2,
  },
  metaValue: {
    fontSize: 7.5,
    fontFamily: "Helvetica-Bold",
    color: "#111827",
  },
  parties: {
    flexDirection: "row",
    gap: 10,
    marginBottom: 7,
  },
  partyBox: {
    flex: 1,
    minHeight: 72,
    padding: 7,
    borderWidth: 1,
    borderColor: "#d1d5db",
    borderRadius: 5,
    backgroundColor: "#ffffff",
  },
  partyName: {
    fontSize: 8,
    fontFamily: "Helvetica-Bold",
    color: "#111827",
    marginBottom: 3,
  },
  irnBlock: {
    marginBottom: 7,
    padding: 5,
    backgroundColor: "#ffffff",
    borderLeftWidth: 1,
    borderLeftColor: "#d1d5db",
  },
  irn: {
    fontSize: 6.5,
    color: "#4b5563",
    lineHeight: 1.35,
  },
  table: {
    borderWidth: 1,
    borderColor: "#d1d5db",
    borderRadius: 4,
    marginBottom: 4,
  },
  tableRow: {
    flexDirection: "row",
    borderBottomWidth: 0.5,
    borderBottomColor: "#e5e7eb",
    minHeight: 11,
    alignItems: "center",
    paddingVertical: 1,
  },
  tableHeader: {
    minHeight: 16,
    paddingVertical: 3,
    backgroundColor: "#f3f4f6",
    fontFamily: "Helvetica-Bold",
    color: "#111827",
  },
  itemSerialNumbers: {
    marginTop: 1,
    color: "#4b5563",
    fontSize: 5.5,
    lineHeight: 1.2,
  },
  epcSerialNumbers: {
    marginTop: 5,
    marginBottom: 6,
  },
  epcSerialNumbersHeading: {
    marginBottom: 3,
    color: "#4b5563",
    fontSize: 7,
    fontFamily: "Helvetica-Bold",
    textTransform: "uppercase",
  },
  epcSerialNumberGroup: {
    marginBottom: 3,
    paddingVertical: 2,
    borderBottomWidth: 0.5,
    borderBottomColor: "#e5e7eb",
  },
  epcSerialNumberName: {
    color: "#111827",
    fontSize: 6.5,
    fontFamily: "Helvetica-Bold",
  },
  epcSerialNumberList: {
    marginTop: 1,
    color: "#4b5563",
    fontSize: 6,
    lineHeight: 1.25,
  },
  colSr: { width: "5%", textAlign: "center", fontSize: 6.5 },
  colDesc: { width: "33%", paddingHorizontal: 4, fontSize: 6.5 },
  itemDescription: { fontSize: 6.5 },
  colHsn: { width: "12%", textAlign: "center", fontSize: 6.5 },
  colQty: { width: "10%", textAlign: "right", paddingRight: 4, fontSize: 6.5 },
  colRate: { width: "13%", textAlign: "right", paddingRight: 4, fontSize: 6.5 },
  colGst: { width: "9%", textAlign: "right", paddingRight: 4, fontSize: 6.5 },
  colAmt: { width: "18%", textAlign: "right", paddingRight: 6, fontSize: 6.5 },
  totalsLayout: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "flex-start",
    marginTop: 4,
  },
  taxNote: {
    width: "48%",
    paddingTop: 5,
    fontSize: 6.5,
    lineHeight: 1.35,
    color: "#6b7280",
  },
  totalsBox: {
    width: "48%",
    padding: 7,
    borderWidth: 1,
    borderColor: "#d1d5db",
    borderRadius: 5,
    backgroundColor: "#ffffff",
  },
  totalRow: {
    flexDirection: "row",
    justifyContent: "space-between",
    marginBottom: 3,
    fontSize: 7,
    color: "#4b5563",
  },
  grandTotal: {
    flexDirection: "row",
    justifyContent: "space-between",
    marginTop: 2,
    paddingTop: 5,
    borderTopWidth: 1,
    borderTopColor: "#d1d5db",
    color: "#111827",
    fontSize: 8.5,
    fontFamily: "Helvetica-Bold",
  },
  amountWords: {
    marginTop: 5,
    padding: 5,
    borderWidth: 0.5,
    borderColor: "#d1d5db",
    borderRadius: 4,
    backgroundColor: "#ffffff",
  },
  amountWordsLabel: {
    marginBottom: 3,
    color: "#6b7280",
    fontSize: 6,
    fontFamily: "Helvetica-Bold",
    textTransform: "uppercase",
  },
  amountWordsText: {
    color: "#111827",
    fontSize: 7,
    fontFamily: "Helvetica-Bold",
    lineHeight: 1.25,
  },
  invoiceClosing: {
    flexDirection: "row",
    justifyContent: "space-between",
    marginTop: 7,
    paddingTop: 5,
    borderTopWidth: 0.5,
    borderTopColor: "#d1d5db",
  },
  bankDetails: {
    width: "57%",
    paddingRight: 8,
  },
  bankHeading: {
    marginBottom: 3,
    color: "#4b5563",
    fontSize: 6.5,
    fontFamily: "Helvetica-Bold",
    textTransform: "uppercase",
  },
  bankText: {
    color: "#4b5563",
    fontSize: 6,
    lineHeight: 1.3,
  },
  signature: {
    width: "40%",
    alignItems: "flex-end",
    justifyContent: "flex-end",
  },
  signatureCompany: {
    color: "#111827",
    fontSize: 6,
    fontFamily: "Helvetica-Bold",
    textAlign: "right",
  },
  stamp: {
    width: 48,
    height: 48,
    objectFit: "contain",
    marginTop: 2,
    marginBottom: 1,
  },
  signatureLabel: {
    color: "#4b5563",
    fontSize: 6,
    textAlign: "right",
  },
  footer: {
    position: "absolute",
    bottom: 22,
    left: 30,
    right: 30,
    paddingTop: 4,
    borderTopWidth: 0.5,
    borderTopColor: "#d1d5db",
    flexDirection: "row",
    justifyContent: "space-between",
    fontSize: 6.5,
    color: "#6b7280",
  },
  computerGenerated: {
    position: "absolute",
    bottom: 8,
    left: 30,
    right: 30,
    color: "#6b7280",
    fontSize: 6,
    textAlign: "center",
  },
  ewbHero: {
    padding: 14,
    marginBottom: 12,
    borderRadius: 6,
    backgroundColor: "#ffffff",
    borderWidth: 1,
    borderColor: "#d1d5db",
    color: "#111827",
  },
  ewbLabel: {
    fontSize: 7,
    color: "#6b7280",
    textTransform: "uppercase",
    marginBottom: 5,
  },
  ewbNumber: {
    fontSize: 18,
    fontFamily: "Helvetica-Bold",
    color: "#111827",
  },
  ewbMeta: {
    flexDirection: "row",
    gap: 8,
    marginTop: 12,
  },
  ewbMetaCell: {
    flexGrow: 1,
    flexBasis: 0,
    padding: 8,
    borderRadius: 4,
    backgroundColor: "#f9fafb",
  },
  addressBox: {
    flex: 1,
    padding: 10,
    borderWidth: 1,
    borderColor: "#d1d5db",
    borderRadius: 5,
  },
  ewbGoodsTable: {
    marginTop: 12,
    borderWidth: 1,
    borderColor: "#d1d5db",
    borderRadius: 4,
  },
  ewbGoodsRow: {
    flexDirection: "row",
    alignItems: "center",
    minHeight: 17,
    paddingVertical: 2,
    borderBottomWidth: 0.5,
    borderBottomColor: "#e5e7eb",
  },
  ewbGoodsHeader: {
    minHeight: 21,
    paddingVertical: 4,
    backgroundColor: "#f3f4f6",
    fontFamily: "Helvetica-Bold",
    color: "#111827",
  },
  ewbColHsn: { width: "12%", paddingLeft: 5, fontSize: 6.5 },
  ewbColDescription: { width: "42%", paddingHorizontal: 4, fontSize: 6.5 },
  ewbColQuantity: {
    width: "12%",
    textAlign: "right",
    paddingRight: 4,
    fontSize: 6.5,
  },
  ewbColTaxable: {
    width: "17%",
    textAlign: "right",
    paddingRight: 4,
    fontSize: 6.5,
  },
  ewbColTaxRate: {
    width: "17%",
    textAlign: "right",
    paddingRight: 5,
    fontSize: 6.5,
  },
  ewbGoodsTotals: {
    flexDirection: "row",
    justifyContent: "space-between",
    padding: 7,
    backgroundColor: "#ffffff",
  },
  ewbGoodsTotalLine: {
    flexDirection: "row",
    justifyContent: "space-between",
    marginBottom: 3,
    fontSize: 7,
    color: "#4b5563",
  },
  ewbGoodsTotalValue: {
    fontSize: 8,
    fontFamily: "Helvetica-Bold",
    color: "#111827",
  },
  ewbTransportBox: {
    marginTop: 5,
    marginBottom: 8,
    paddingHorizontal: 8,
    paddingVertical: 4,
    borderWidth: 1,
    borderColor: "#d1d5db",
    borderRadius: 5,
    backgroundColor: "#ffffff",
  },
  ewbTransportRow: {
    flexDirection: "row",
    alignItems: "center",
    paddingVertical: 3,
    borderBottomWidth: 0.5,
    borderBottomColor: "#e5e7eb",
  },
  ewbTransportLabel: {
    width: "34%",
    color: "#6b7280",
    fontSize: 6.5,
  },
  ewbTransportValue: {
    flex: 1,
    color: "#111827",
    fontSize: 7.5,
    fontFamily: "Helvetica-Bold",
  },
  ewbGoodsTotalLabel: {
    color: "#6b7280",
    fontSize: 6,
    textTransform: "uppercase",
  },
  ewbLinkedDetails: {
    marginTop: 8,
    marginBottom: 3,
    padding: 6,
    borderLeftWidth: 3,
    borderLeftColor: "#d1d5db",
    backgroundColor: "#ffffff",
  },
  ewbLinkedText: {
    color: "#4b5563",
    fontSize: 6.5,
    lineHeight: 1.3,
  },
});

interface InvoiceDocumentProps {
  data: InvoiceData;
  shippingAddress?: IOrderAddress;
  invoiceType?: "ORDER" | "EPC";
}

const formatAmount = (value?: number | string | null) => {
  const amount = Number(value);
  return Number.isFinite(amount)
    ? amount.toLocaleString("en-IN", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      })
    : "0.00";
};

const numberInWords = (value: number): string => {
  const ones = [
    "Zero",
    "One",
    "Two",
    "Three",
    "Four",
    "Five",
    "Six",
    "Seven",
    "Eight",
    "Nine",
    "Ten",
    "Eleven",
    "Twelve",
    "Thirteen",
    "Fourteen",
    "Fifteen",
    "Sixteen",
    "Seventeen",
    "Eighteen",
    "Nineteen",
  ];
  const tens = [
    "",
    "",
    "Twenty",
    "Thirty",
    "Forty",
    "Fifty",
    "Sixty",
    "Seventy",
    "Eighty",
    "Ninety",
  ];

  if (value < 20) return ones[value];
  if (value < 100) {
    return `${tens[Math.floor(value / 10)]}${value % 10 ? ` ${ones[value % 10]}` : ""}`;
  }

  for (const [unit, label] of [
    [10000000, "Crore"],
    [100000, "Lakh"],
    [1000, "Thousand"],
    [100, "Hundred"],
  ] as const) {
    if (value >= unit) {
      const count = Math.floor(value / unit);
      const remainder = value % unit;
      return `${numberInWords(count)} ${label}${remainder ? ` ${numberInWords(remainder)}` : ""}`;
    }
  }

  return "Zero";
};

const amountInWords = (value: number | string) => {
  const amount = Number(value);
  if (!Number.isFinite(amount)) return "INR Zero Only";

  const totalPaise = Math.round(Math.abs(amount) * 100);
  const rupees = Math.floor(totalPaise / 100);
  const paise = totalPaise % 100;
  const sign = amount < 0 ? "Minus " : "";
  const paiseText = paise ? ` and ${numberInWords(paise)} Paise` : "";

  return `INR ${sign}${numberInWords(rupees)}${paiseText} Only`;
};

const formatDate = (value?: string | Date | null) => {
  if (!value) return "-";
  if (value instanceof Date) {
    return Number.isFinite(value.getTime())
      ? value.toLocaleDateString("en-IN", {
          day: "2-digit",
          month: "short",
          year: "numeric",
          timeZone: "UTC",
        })
      : "-";
  }

  const dateOnly = value.match(/^\d{2}\/\d{2}\/\d{4}/)?.[0];
  if (dateOnly) return dateOnly;
  const date = new Date(value);
  return Number.isFinite(date.getTime())
    ? date.toLocaleDateString("en-IN", {
        day: "2-digit",
        month: "short",
        year: "numeric",
        timeZone: "UTC",
      })
    : value;
};

const transportModes: Record<string, string> = {
  "1": "Road",
  "2": "Rail",
  "3": "Air",
  "4": "Ship",
};

export const InvoiceDocument: React.FC<InvoiceDocumentProps> = ({
  data,
  shippingAddress,
  invoiceType = "ORDER",
}) => {
  const extracted =
    data?.extractedInvoice ||
    data?.taxProResponse?.ExtractedSignedInvoiceData ||
    {};
  const seller = extracted?.SellerDtls || {};
  const buyer = extracted?.BuyerDtls || {};
  const dispatch = extracted?.DispDtls || seller;
  const shipTo = extracted?.ShipDtls;
  const valDtls = extracted?.ValDtls || {};
  const items = extracted?.ItemList || [];
  const epcSerialNumberGroups =
    invoiceType === "EPC"
      ? (data?.serialNumbers || []).filter(
          (group) =>
            Array.isArray(group.serialNumbers) &&
            group.serialNumbers.some(
              (serialNumber) =>
                typeof serialNumber === "string" &&
                serialNumber.trim().length > 0,
            ),
        )
      : [];
  const serialNumbersByProductName = new Map<string, string[]>();
  (data?.goods || []).forEach((group) => {
    const productName =
      group.type === "product"
        ? group.product?.productName || group.product?.name
        : group.rawMaterial?.name || group.rawMaterial?.productName;
    const normalizedName = productName?.trim().toLowerCase();
    const serialNumbers = Array.isArray(group.serialNumbers)
      ? group.serialNumbers.filter(
          (serialNumber) =>
            typeof serialNumber === "string" && serialNumber.trim().length > 0,
        )
      : [];

    if (!normalizedName || serialNumbers.length === 0) return;
    const existingSerialNumbers =
      serialNumbersByProductName.get(normalizedName) || [];
    serialNumbersByProductName.set(normalizedName, [
      ...existingSerialNumbers,
      ...serialNumbers,
    ]);
  });

  const qrCodeValue = data?.qrCodeImage || data?.taxProResponse?.QrCodeImage;
  const qrCodeBase64 = qrCodeValue
    ? qrCodeValue.startsWith("data:")
      ? qrCodeValue
      : `data:image/png;base64,${qrCodeValue}`
    : null;

  const ewbNo =
    data?.ewbNo ?? data?.taxProResponse?.EwbNo ?? extracted?.EwbDtls?.EwbNo;
  const ewbDate =
    data?.ewbDate ?? data?.taxProResponse?.EwbDt ?? extracted?.EwbDtls?.EwbDt;
  const ewbValidTill =
    data?.ewbValidTill ??
    data?.taxProResponse?.EwbValidTill ??
    extracted?.EwbDtls?.EwbValidTill;
  const ewbDistance =
    data?.ewbDistance ??
    data?.taxProResponse?.Distance ??
    extracted?.EwbDtls?.Distance;
  const ewbVehNo =
    data?.ewbVehNo ?? data?.taxProResponse?.VehNo ?? extracted?.EwbDtls?.VehNo;
  const ewbTransId =
    data?.ewbTransId ??
    data?.taxProResponse?.TransId ??
    extracted?.EwbDtls?.TransId;
  const ewbTransName =
    data?.ewbTransName ??
    data?.taxProResponse?.TransName ??
    extracted?.EwbDtls?.TransName;
  const ewbTransDocNo =
    data?.ewbTransDocNo ??
    data?.taxProResponse?.TransDocNo ??
    extracted?.EwbDtls?.TransDocNo;
  const ewbTransDocDt =
    data?.ewbTransDocDt ??
    data?.taxProResponse?.TransDocDt ??
    extracted?.EwbDtls?.TransDocDt;
  const ewbTransMode =
    data?.ewbTransMode ??
    data?.taxProResponse?.TransMode ??
    extracted?.EwbDtls?.TransMode;
  const ewbVehType =
    data?.ewbVehType ??
    data?.taxProResponse?.VehType ??
    extracted?.EwbDtls?.VehType;
  const shipToLines =
    invoiceType === "ORDER"
      ? [
          shippingAddress?.line1,
          shippingAddress?.line2 || shippingAddress?.address,
          [
            shippingAddress?.city,
            shippingAddress?.state,
            shippingAddress?.pincode,
          ]
            .filter(Boolean)
            .join(", "),
        ].filter(Boolean)
      : [];
  const buyerAddressLines = [buyer.Addr1, buyer.Addr2, buyer.Loc, buyer.Pin]
    .filter(Boolean)
    .map(String);
  const extractedShipToLines = [
    shipTo?.Addr1,
    shipTo?.Addr2,
    shipTo?.Loc,
    shipTo?.Pin,
  ]
    .filter(Boolean)
    .map(String);
  const ewbShipTo = shipToLines.length
    ? shipToLines
    : extractedShipToLines.length
      ? extractedShipToLines
      : buyerAddressLines;
  const igstValue = Number(valDtls.IgstVal) || 0;
  const declaredCgstValue = Number(valDtls.CgstVal) || 0;
  const declaredSgstValue = Number(valDtls.SgstVal) || 0;
  const hasDeclaredSplitTaxes = declaredCgstValue > 0 || declaredSgstValue > 0;
  const cgstValue = hasDeclaredSplitTaxes
    ? declaredCgstValue
    : Number((igstValue / 2).toFixed(2));
  const sgstValue = hasDeclaredSplitTaxes
    ? declaredSgstValue
    : Number((igstValue - cgstValue).toFixed(2));
  const hasTaxValues = cgstValue > 0 || sgstValue > 0;
  const taxLines: [string, number][] = hasTaxValues
    ? [
        ["CGST", cgstValue],
        ["SGST", sgstValue],
      ]
    : [];
  const transportPrice = Number(data?.transportPrice);
  if (Number.isFinite(transportPrice) && transportPrice > 0) {
    taxLines.push(["Transport", transportPrice]);
  }
  const netMeterPayment = Number(data?.netMeterPayment);
  if (
    data?.netMeterPayment != null &&
    Number.isFinite(netMeterPayment) &&
    netMeterPayment !== 0
  ) {
    taxLines.push(["Net Meter Charges", netMeterPayment]);
  }
  const irn = data?.irn || data?.taxProResponse?.Irn || extracted?.Irn;
  const invoiceNumber = extracted?.DocDtls?.No || data?.invoiceNumber || "-";
  const invoiceDate = formatDate(extracted?.DocDtls?.Dt);
  const totalAmount = valDtls.TotInvVal ?? 0;
  const transporter = ewbTransName || ewbTransId || "-";
  const acknowledgementNumber =
    data?.ackNo ?? data?.taxProResponse?.AckNo ?? extracted?.AckNo;
  const acknowledgementDate = formatDate(
    data?.ackDate ?? data?.taxProResponse?.AckDt ?? extracted?.AckDt,
  );

  return (
    <Document>
      <Page size="A4" style={styles.page}>
        <View style={styles.topLine}>
          <View style={styles.seller}>
            <Image style={styles.logo} src={logo} />
            <Text style={styles.sellerName}>
              {seller.LglNm || "FRELIT ENERGY PRIVATE LIMITED"}
            </Text>
            <Text style={styles.muted}>
              {[seller.Addr1, seller.Addr2, seller.Loc, seller.Pin]
                .filter(Boolean)
                .join(", ")}
            </Text>
            <Text style={styles.muted}>GSTIN: {seller.Gstin || "-"}</Text>
            {(seller.Ph || seller.Em) && (
              <Text style={styles.muted}>
                {[seller.Ph, seller.Em].filter(Boolean).join("  |  ")}
              </Text>
            )}
          </View>
          {qrCodeBase64 && (
            <View style={styles.qrBlock}>
              <Image style={styles.qrImage} src={qrCodeBase64} />
            </View>
          )}
        </View>

        <View style={styles.row}>
          <View style={{ flex: 1 }}>
            <Text style={styles.documentTitle}>Tax Invoice</Text>
            <Text style={styles.documentSubtitle}>e-Invoice</Text>
          </View>
          <View style={{ alignItems: "flex-end" }}>
            <Text style={styles.muted}>INVOICE TOTAL</Text>
            <Text style={[styles.documentTitle, { fontSize: 15 }]}>
              INR {formatAmount(totalAmount)}
            </Text>
          </View>
        </View>

        <View style={styles.metaGrid}>
          {[
            ["Invoice number", invoiceNumber],
            ["Invoice date", invoiceDate],
            ["E-way bill", ewbNo ? String(ewbNo) : "-"],
          ].map(([label, value], index) => (
            <View
              key={label}
              style={[
                styles.metaCell,
                index === 2 ? { borderRightWidth: 0 } : {},
              ]}
            >
              <Text style={styles.metaLabel}>{label}</Text>
              <Text style={styles.metaValue}>{value}</Text>
            </View>
          ))}
        </View>

        <View style={styles.parties}>
          <View style={styles.partyBox}>
            <Text style={styles.sectionHeading}>Bill to</Text>
            <Text style={styles.partyName}>
              {buyer.LglNm || buyer.TrdNm || "-"}
            </Text>
            {buyerAddressLines.map((line, index) => (
              <Text style={styles.muted} key={`bill-${index}`}>
                {line}
              </Text>
            ))}
            <Text style={styles.muted}>GSTIN: {buyer.Gstin || "-"}</Text>
            <Text style={styles.muted}>
              Place of supply: {buyer.Pos || "-"}
            </Text>
          </View>
          <View style={styles.partyBox}>
            <Text style={styles.sectionHeading}>Ship to</Text>
            <Text style={styles.partyName}>
              {shipTo?.LglNm ||
                shipTo?.TrdNm ||
                buyer.LglNm ||
                buyer.TrdNm ||
                "-"}
            </Text>
            {(shipToLines.length
              ? shipToLines
              : ewbShipTo.length
                ? ewbShipTo
                : buyerAddressLines
            ).map((line, index) => (
              <Text style={styles.muted} key={`${line}-${index}`}>
                {line}
              </Text>
            ))}
            <Text style={styles.muted}>GSTIN: {buyer.Gstin || "-"}</Text>
          </View>
        </View>

        {(irn || acknowledgementNumber || acknowledgementDate !== "-") && (
          <View style={styles.irnBlock}>
            {irn && <Text style={styles.irn}>IRN: {irn}</Text>}
            {acknowledgementNumber && (
              <Text style={styles.irn}>
                Acknowledgement: {String(acknowledgementNumber)}
                {acknowledgementDate !== "-"
                  ? `  |  ${acknowledgementDate}`
                  : ""}
              </Text>
            )}
          </View>
        )}

        <View style={styles.table}>
          <View style={[styles.tableRow, styles.tableHeader]} fixed>
            <Text style={styles.colSr}>#</Text>
            <Text style={styles.colDesc}>Description</Text>
            <Text style={styles.colHsn}>HSN/SAC</Text>
            <Text style={styles.colQty}>Qty</Text>
            <Text style={styles.colRate}>Rate</Text>
            <Text style={styles.colGst}>GST</Text>
            <Text style={styles.colAmt}>Amount</Text>
          </View>
          {items.map((item, index) => {
            const serialNumbers = serialNumbersByProductName.get(
              item.PrdDesc?.trim().toLowerCase() || "",
            );

            return (
              <View
                style={styles.tableRow}
                key={`${item.SlNo || item.ItemNo || index}`}
                wrap={false}
              >
                <Text style={styles.colSr}>{index + 1}</Text>
                <View style={styles.colDesc}>
                  <Text style={styles.itemDescription}>
                    {item.PrdDesc || "-"}
                  </Text>
                  {serialNumbers?.length ? (
                    <Text style={styles.itemSerialNumbers}>
                      S/N: {serialNumbers.join(", ")}
                    </Text>
                  ) : null}
                </View>
                <Text style={styles.colHsn}>{item.HsnCd}</Text>
                <Text style={styles.colQty}>{`${item.Qty} ${item.Unit}`}</Text>
                <Text style={styles.colRate}>
                  {formatAmount(item.UnitPrice)}
                </Text>
                <Text style={styles.colGst}>{`${item.GstRt || 0}%`}</Text>
                <Text style={[styles.colAmt, styles.bold]}>
                  {formatAmount(item.AssAmt)}
                </Text>
              </View>
            );
          })}
        </View>

        {epcSerialNumberGroups.length > 0 && (
          <View style={styles.epcSerialNumbers}>
            <Text style={styles.epcSerialNumbersHeading}>Serial numbers</Text>
            {epcSerialNumberGroups.map((group, index) => {
              const productName =
                group.productName?.trim() || group.name?.trim();
              const productLabel =
                group.type === "material" ? "Material" : "Product";
              const serialNumbers = group.serialNumbers.filter(
                (serialNumber) =>
                  typeof serialNumber === "string" &&
                  serialNumber.trim().length > 0,
              );

              return (
                <View
                  key={`${group.type}-${productName || index}`}
                  style={styles.epcSerialNumberGroup}
                >
                  <Text style={styles.epcSerialNumberName}>
                    {productLabel}: {productName || `${productLabel} details`}
                  </Text>
                  <Text style={styles.epcSerialNumberList}>
                    {serialNumbers.join(", ")}
                  </Text>
                </View>
              );
            })}
          </View>
        )}

        <View break={items.length > 30} wrap={false}>
          <View style={styles.totalsLayout}>
            <View style={styles.taxNote}>
              <Text>Amounts are shown in Indian Rupees (INR).</Text>
            </View>
            <View style={styles.totalsBox}>
              <View style={styles.totalRow}>
                <Text>Taxable value</Text>
                <Text>INR {formatAmount(valDtls.AssVal)}</Text>
              </View>
              {taxLines.map(([label, value]) => (
                <View style={styles.totalRow} key={label}>
                  <Text>{label}</Text>
                  <Text>INR {formatAmount(value)}</Text>
                </View>
              ))}
              {Number(valDtls.RndOffAmt) !== 0 && (
                <View style={styles.totalRow}>
                  <Text>Round off</Text>
                  <Text>INR {formatAmount(valDtls.RndOffAmt)}</Text>
                </View>
              )}
              <View style={styles.grandTotal}>
                <Text>Total payable</Text>
                <Text>INR {formatAmount(totalAmount)}</Text>
              </View>
            </View>
          </View>

          <View style={styles.amountWords}>
            <Text style={styles.amountWordsLabel}>
              Amount chargeable in words
            </Text>
            <Text style={styles.amountWordsText}>
              {amountInWords(totalAmount)}
            </Text>
          </View>

          <View style={styles.invoiceClosing}>
            <View style={styles.bankDetails}>
              <Text style={styles.bankHeading}>Frelit bank details</Text>
              <Text style={styles.bankText}>
                A/c Holder: FRELIT ENERGY PRIVATE LIMITED
              </Text>
              <Text style={styles.bankText}>Bank: Axis Bank CC A/c-7928</Text>
              <Text style={styles.bankText}>A/c No.: 922030048197928</Text>
              <Text style={styles.bankText}>
                Branch: Maneja | IFSC: UTIB0002999
              </Text>
            </View>
            <View style={styles.signature}>
              <Text style={styles.signatureCompany}>
                For FRELIT ENERGY PRIVATE LIMITED
              </Text>
              <Image style={styles.stamp} src={stamp} />
              <Text style={styles.signatureLabel}>Authorised Signatory</Text>
            </View>
          </View>
        </View>

        <View style={styles.footer} fixed>
          <Text>Frelit Energy Private Limited</Text>
          <Text>Tax Invoice | {invoiceNumber}</Text>
        </View>
        <Text style={styles.computerGenerated} fixed>
          This is a computer-generated tax invoice.
        </Text>
      </Page>

      {Boolean(ewbNo) && (
        <Page size="A4" style={[styles.page, styles.ewbPage]}>
          <View style={styles.topLine}>
            <View>
              <Text style={styles.documentTitle}>E-Way Bill</Text>
              <Text style={styles.documentSubtitle}>
                Transportation document
              </Text>
            </View>
            {qrCodeBase64 && (
              <View style={styles.qrBlock}>
                <Image style={styles.qrImage} src={qrCodeBase64} />
              </View>
            )}
          </View>

          <View style={styles.ewbHero}>
            <Text style={styles.ewbLabel}>E-way bill number</Text>
            <Text style={styles.ewbNumber}>{String(ewbNo)}</Text>
            <View style={styles.ewbMeta}>
              {[
                ["Generated", formatDate(ewbDate)],
                ["Valid until", formatDate(ewbValidTill)],
                ["Invoice", invoiceNumber],
              ].map(([label, value]) => (
                <View style={styles.ewbMetaCell} key={label}>
                  <Text style={styles.ewbLabel}>{label}</Text>
                  <Text style={{ color: "#111827", fontSize: 8 }}>{value}</Text>
                </View>
              ))}
            </View>
          </View>

          <View style={styles.ewbTransportBox}>
            {[
              ["Transport mode", transportModes[ewbTransMode || ""] || "-"],
              ["Approx. distance", ewbDistance ? `${ewbDistance} km` : "-"],
              ["Transporter", transporter],
              ["Transporter ID", ewbTransId || "-"],
              ["Vehicle number", ewbVehNo || "-"],
              [
                "Vehicle type",
                ewbVehType === "O"
                  ? "Over-dimensional cargo"
                  : ewbVehType === "R"
                    ? "Regular"
                    : "-",
              ],
              ["Transport document", ewbTransDocNo || "-"],
              ["Document date", formatDate(ewbTransDocDt)],
            ].map(([label, value]) => (
              <View style={styles.ewbTransportRow} key={label}>
                <Text style={styles.ewbTransportLabel}>{label}</Text>
                <Text style={styles.ewbTransportValue}>{value}</Text>
              </View>
            ))}
          </View>

          <Text style={styles.sectionHeading}>Movement of goods</Text>
          <View style={styles.row}>
            <View style={styles.addressBox}>
              <Text style={styles.sectionHeading}>From / Dispatch from</Text>
              <Text style={styles.partyName}>Frelit Energy Pvt. Ltd.</Text>
              {[dispatch.Addr1, dispatch.Addr2, dispatch.Loc, dispatch.Pin]
                .filter(Boolean)
                .map((line, index) => (
                  <Text style={styles.muted} key={`${line}-${index}`}>
                    {line}
                  </Text>
                ))}
              <Text style={styles.muted}>GSTIN: {seller.Gstin || "-"}</Text>
            </View>
            <View style={styles.addressBox}>
              <Text style={styles.sectionHeading}>To / Ship to</Text>
              <Text style={styles.partyName}>
                {shipTo?.LglNm ||
                  shipTo?.TrdNm ||
                  buyer.LglNm ||
                  buyer.TrdNm ||
                  "-"}
              </Text>
              {ewbShipTo.map((line, index) => (
                <Text style={styles.muted} key={`${line}-${index}`}>
                  {line}
                </Text>
              ))}
              <Text style={styles.muted}>GSTIN: {buyer.Gstin || "-"}</Text>
            </View>
          </View>

          <View style={styles.ewbLinkedDetails}>
            <Text style={styles.ewbLinkedText}>
              Linked invoice: {invoiceNumber} | Invoice date: {invoiceDate}
            </Text>
            {irn && <Text style={styles.ewbLinkedText}>IRN: {irn}</Text>}
          </View>

          <Text style={[styles.sectionHeading, { marginTop: 12 }]}>
            Goods details
          </Text>
          <View style={styles.ewbGoodsTable}>
            <View style={[styles.ewbGoodsRow, styles.ewbGoodsHeader]} fixed>
              <Text style={styles.ewbColHsn}>HSN</Text>
              <Text style={styles.ewbColDescription}>
                Product name &amp; description
              </Text>
              <Text style={styles.ewbColQuantity}>Quantity</Text>
              <Text style={styles.ewbColTaxable}>Taxable amt</Text>
              <Text style={styles.ewbColTaxRate}>Tax rate</Text>
            </View>
            {items.map((item, index) => {
              const taxRate = Number(item.GstRt) || 0;
              const halfRate = Number((taxRate / 2).toFixed(2));
              const taxRateLabel = `${halfRate}% CGST\n${halfRate}% SGST`;

              return (
                <View
                  style={styles.ewbGoodsRow}
                  key={`ewb-${item.SlNo || item.ItemNo || index}`}
                  wrap={false}
                >
                  <Text style={styles.ewbColHsn}>{item.HsnCd || "-"}</Text>
                  <Text style={styles.ewbColDescription}>
                    {item.PrdDesc || "-"}
                  </Text>
                  <Text style={styles.ewbColQuantity}>
                    {`${item.Qty} ${item.Unit}`}
                  </Text>
                  <Text style={styles.ewbColTaxable}>
                    {formatAmount(item.AssAmt)}
                  </Text>
                  <Text style={styles.ewbColTaxRate}>{taxRateLabel}</Text>
                </View>
              );
            })}
            <View style={styles.ewbGoodsTotals}>
              <View style={{ width: "58%" }}>
                {hasTaxValues && (
                  <View style={styles.ewbGoodsTotalLine}>
                    <Text>CGST amount</Text>
                    <Text>INR {formatAmount(cgstValue)}</Text>
                  </View>
                )}
                {hasTaxValues && (
                  <View style={styles.ewbGoodsTotalLine}>
                    <Text>SGST amount</Text>
                    <Text>INR {formatAmount(sgstValue)}</Text>
                  </View>
                )}
              </View>
              <View
                style={{ alignItems: "flex-end", justifyContent: "center" }}
              >
                <Text style={styles.ewbGoodsTotalLabel}>
                  Total taxable amount
                </Text>
                <Text style={styles.ewbGoodsTotalValue}>
                  INR {formatAmount(valDtls.AssVal)}
                </Text>
                <Text style={[styles.ewbGoodsTotalLabel, { marginTop: 5 }]}>
                  Total invoice amount
                </Text>
                <Text style={styles.ewbGoodsTotalValue}>
                  INR {formatAmount(totalAmount)}
                </Text>
              </View>
            </View>
          </View>

          <View style={styles.footer} fixed>
            <Text>Frelit Energy Private Limited</Text>
            <Text>E-Way Bill | {String(ewbNo)}</Text>
          </View>
        </Page>
      )}
    </Document>
  );
};
